/**
 * tutorManageSessionAdapter — « Gérer une séance » (`move-session`) : la séance du masteur, ce que
 * son absence change (`absence-impact`), ses crédits d'absence (`substitution-absence-summary`), les
 * créneaux qu'il peut proposer en rattrapage, et ce que chaque déclaration envoie.
 *
 * Ports littéraux de symboles de `src/tutor/**` — interdits à `ports/**` — recopiés, avec leur fichier :
 *  - `emptiesSession`, `tutorChangeIds` — `tutor/lives/absenceImpact.js` ;
 *  - `buildSessionsList` (les rattrapages), le décompte dynamique des jours d'absence, la règle
 *    `shouldSkipMakeupSteps`, le corps de `onSubmit` — `tutor/lives/ManageRDVScreen.js` ;
 *  - `RELEASABLE_ROOM_TYPES` — `tutor/lives/SelectSituationComponent.js` ; `ONE_OFF_ROOM_TYPES`,
 *    `oneOffLabel` — `tutor/availability/releaseSlotsModel.js` (son `dayTimeLabel` est celui de
 *    `tutorReleaseAdapter`).
 * Tant que l'ancien écran vit derrière l'interrupteur, ces symboles sont une seconde copie : une
 * évolution d'une règle se reporte ici.
 *
 * Jamais importé par le DS.
 */
import moment from "moment"
import "moment/locale/fr"

import {
    type UITutorAbsenceCredits,
    type UITutorAbsenceImpactStudent,
    type UITutorMakeupOption,
    type UITutorRescheduleReason,
} from "../../../ui/types/tutorLives"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { dayTimeLabel } from "../Availability/tutorReleaseAdapter"
import {
    toTutorWeekSessions,
    type ApiTutorLiveSession,
    type ApiTutorSessionStudent,
    type UITutorHomeSession,
} from "../Home/tutorSessionAdapter"
import { slotDayOf, WEEKDAY_ORDER } from "../shared/tutorWeekAdapter"

// ── Formes du serveur ───────────────────────────────────────────────────────

/** Une séance du compte, avec ce que « Gérer une séance » lit en plus de l'accueil. */
export type ApiManagedLiveSession = ApiTutorLiveSession & {
    /** « Tuesday 17:00 » : le créneau régulier. */
    day_time?: string | null
    /** « Jules M. 4ème, Léa B. 4ème » — la ligne des rattrapages. */
    students_list_string?: string | null
    live_room?: (ApiTutorLiveSession["live_room"] & { supports_no_show_recovery?: boolean | null }) | null
    students?: (ApiTutorSessionStudent & { firstname?: string })[] | null
}

/** Un élève de `GET live-sessions/{id}/absence-impact` (`TutorAbsenceRelationRule`). */
export type ApiAbsenceImpactStudent = {
    id?: string | number
    firstname?: string | null
    outcome?: string | null
    reason?: string | null
    touched_occurrences?: number | null
    short_notice?: boolean | null
}

/** `GET tutor/substitution-absence-summary`. */
export type ApiAbsenceSummary = {
    total_days?: number | null
    limit?: number | null
    is_new_tutor?: boolean | null
    /** Les jours déjà absents du mois, en clés « YYYY-MM-DD ». */
    dates?: Record<string, unknown> | null
    next_month?: { total_days?: number | null; dates?: Record<string, unknown> | null } | null
    hypercare_block?: {
        has_hypercare_students?: boolean | null
        message?: string | null
        support_whatsapp_url?: string | null
    } | null
}

// ── La séance ───────────────────────────────────────────────────────────────

/** Les lives que « Libérer des créneaux » reprend sur leur créneau régulier (`SelectSituationComponent`). */
const RELEASABLE_ROOM_TYPES = ["biweekly", "study_group", "individual", "holiday_workbook"]
/** Un essai ou un stage s'y ouvre aussi, sur l'heure du live (`releaseSlotsModel`). */
const ONE_OFF_ROOM_TYPES = ["trial", "holiday_course"]

/** La raison qu'une absence envoie : l'ancien formulaire ne la montrait pas, il l'envoyait d'office. */
export const TUTOR_ABSENCE_REASON = "Autre raison (préciser)"

/** « Tuesday 18:00 » → « mardi à 18h » (`dayTimeLabel`) ; `null` sans créneau lisible. */
function slotLabelOf(dayTime: string | null | undefined): string | null {
    const { weekday, time } = slotOf(dayTime)
    return dayTime != null && weekday != null && time != null ? dayTimeLabel(dayTime) : null
}

export type TutorManagedSession = {
    session: UITutorHomeSession
    raw: ApiManagedLiveSession
    isIndividual: boolean
    isTrial: boolean
    /** Le retard ne se signale que le jour même. */
    canReportDelay: boolean
    substitutionAccepted: boolean
    /** « le mardi à 18h » — le créneau régulier. */
    slotLabel: string
    /** Le premier élève de la séance : l'exemple des consignes, l'accord de l'élève. */
    firstStudentName: string
    /** « YYYY-MM-DD HH:mm » : la séance visée (`previous_date`, la date de l'absence). */
    occurrenceDate: string
    /** La date d'une séance déjà replanifiée du créneau : « Veux-tu écraser ce changement ? ». */
    rescheduledDate?: string
    /** « Plus disponible » : où l'écran sort. */
    release: { liveSessionId: string | number; oneOffLabel?: string } | null
}

/**
 * La séance de la route (`liveId`), au jour de sa plus proche occurrence — ou de `dateTime` quand la
 * route le donne (l'ancien paramètre, que plus aucun appelant ne passe). `null` : pas dans le compte.
 */
export function toTutorManagedSession(
    liveSessions: ReadonlyArray<ApiManagedLiveSession | null | undefined> | null | undefined,
    liveId: string | number,
    schoolSubjects?: ReadonlyArray<ApiSchoolSubject | null | undefined> | null,
    dateTime?: string | null,
    now: moment.Moment | Date | number = moment()
): TutorManagedSession | null {
    const raw = (liveSessions ?? []).find((candidate) => String(candidate?.id) === String(liveId))
    const mapped = toTutorWeekSessions(raw != null ? [raw] : [], schoolSubjects, now)[0]
    if (raw == null || mapped == null) return null

    const target = dateTime ? (raw.next_occurrences ?? []).find((occ) => occ?.date === dateTime) : undefined
    const session: UITutorHomeSession =
        target != null && dateTime
            ? {
                  ...mapped,
                  date: moment(dateTime).format("YYYY-MM-DDTHH:mm:ss"),
                  occurrenceDate: moment(dateTime).format("YYYY-MM-DD HH:mm"),
              }
            : mapped
    // Un remplacement accepté ôte « plus disponible » et « autre » (`isReplacement && isAccepted`).
    const substitutionAccepted =
        target != null ? target.substitution_id != null && target.substitute_id != null : !!mapped.substitutionAccepted

    const roomType = raw.live_room?.type ?? null
    const slot = slotLabelOf(raw.day_time)
    const first = raw.students?.[0]
    const rescheduled = (raw.next_occurrences ?? []).find((occ) => occ?.rescheduling_id != null)
    const release =
        roomType != null && RELEASABLE_ROOM_TYPES.includes(roomType)
            ? { liveSessionId: raw.id as string | number }
            : roomType != null && ONE_OFF_ROOM_TYPES.includes(roomType)
              ? {
                    liveSessionId: raw.id as string | number,
                    oneOffLabel: `${roomType === "trial" ? "essai" : "stage"} du ${slot ?? ""}`,
                }
              : null

    return {
        session,
        raw,
        isIndividual: roomType === "individual",
        isTrial: roomType === "trial",
        canReportDelay: moment(session.date).isSame(moment(now), "day"),
        substitutionAccepted,
        slotLabel: slot != null ? `le ${slot}` : "",
        firstStudentName: first?.firstname ?? first?.display_name ?? "l'élève",
        occurrenceDate: session.occurrenceDate,
        ...(rescheduled?.date ? { rescheduledDate: moment(rescheduled.date).format("YYYY-MM-DDTHH:mm:ss") } : {}),
        release,
    }
}

// ── L'absence ───────────────────────────────────────────────────────────────

export function toUIAbsenceImpact(
    students: ReadonlyArray<ApiAbsenceImpactStudent | null | undefined> | null | undefined
): UITutorAbsenceImpactStudent[] {
    return (students ?? [])
        .map((student): UITutorAbsenceImpactStudent | null => {
            if (student?.id == null) return null
            const reason =
                student.reason === "relation_not_started" || student.reason === "nascent_relation_long_absence"
                    ? student.reason
                    : undefined
            return {
                id: student.id,
                firstname: student.firstname ?? "",
                outcome: student.outcome === "tutor_change" ? "tutor_change" : "replaced",
                ...(reason != null ? { reason } : {}),
                ...(student.touched_occurrences != null ? { touchedOccurrences: student.touched_occurrences } : {}),
                ...(student.short_notice ? { shortNotice: true } : {}),
            }
        })
        .filter((student): student is UITutorAbsenceImpactStudent => student != null)
}

/** Tous changent de masteur et aucun ne garde la séance : elle se vide (`emptiesSession`). */
export function emptiesSession(impact: UITutorAbsenceImpactStudent[] | null | undefined): boolean {
    return (
        (impact ?? []).length > 0 &&
        (impact ?? []).every((student) => student.outcome === "tutor_change" && !student.shortNotice)
    )
}

/** Les élèves que l'aperçu a montrés changeant de masteur : la déclaration les renvoie (`tutorChangeIds`). */
export function tutorChangeIds(impact: UITutorAbsenceImpactStudent[] | null | undefined): Array<string | number> {
    return (impact ?? []).filter((student) => student.outcome === "tutor_change").map((student) => student.id)
}

/**
 * Le décompte des crédits, cette demande comprise : la date de l'absence ajoute un jour au mois en
 * cours (ou au suivant) quand elle n'y est pas déjà (`dynamicAbsenceDays`, `dynamicNextMonthDays`).
 * `null` tant que le décompte n'est pas lu.
 */
export function toUIAbsenceCredits(
    summary: ApiAbsenceSummary | null | undefined,
    absenceDate: string,
    now: moment.Moment | Date | number = moment()
): UITutorAbsenceCredits | null {
    if (summary == null) return null
    const at = moment(now)
    const day = moment(absenceDate)
    const key = day.format("YYYY-MM-DD")
    const inCurrentMonth = day.isSameOrBefore(moment(at).endOf("month"), "day")
    const inNextMonth = day.isBetween(
        moment(at).add(1, "month").startOf("month"),
        moment(at).add(1, "month").endOf("month"),
        "day",
        "[]"
    )
    const addsCurrent = inCurrentMonth && !Object.keys(summary.dates ?? {}).includes(key) ? 1 : 0
    const addsNext = inNextMonth && !Object.keys(summary.next_month?.dates ?? {}).includes(key) ? 1 : 0
    const nextMonthTotal = (summary.next_month?.total_days ?? 0) + addsNext
    const hasNextMonth = summary.next_month != null && nextMonthTotal > 0
    return {
        totalDays: (summary.total_days ?? 0) + addsCurrent,
        limit: summary.limit ?? 0,
        ...(summary.is_new_tutor ? { isNewTutor: true } : {}),
        ...(hasNextMonth ? { nextMonthTotalDays: nextMonthTotal } : {}),
        ...(hasNextMonth && addsNext > 0 ? { nextMonthAddsDays: true } : {}),
    }
}

/** Ce que la case de la boîte « Ta présence compte » ouvre, à l'ouverture de l'écran. */
export function hypercareOf(summary: ApiAbsenceSummary | null | undefined): { message: string; url?: string } | null {
    const block = summary?.hypercare_block
    if (!block?.has_hypercare_students) return null
    return { message: block.message ?? "", ...(block.support_whatsapp_url ? { url: block.support_whatsapp_url } : {}) }
}

// ── Les rattrapages ─────────────────────────────────────────────────────────

/** Ce qu'une option de rattrapage envoie (`selected_makeup_occurrences[]`). */
export type MakeupPayload = { liveSessionId: string | number; occurrenceDate: string }

export type TutorMakeupOption = UITutorMakeupOption & { payload: MakeupPayload }

/** La règle du live au rattrapage (`supportsNoShowRecovery`, `data/utility/liveSessionType.js`). */
function supportsNoShowRecovery(liveRoom: ApiManagedLiveSession["live_room"]): boolean {
    return (
        liveRoom?.supports_no_show_recovery ??
        ["biweekly", "study_group", "holiday_workbook"].includes(liveRoom?.type ?? "")
    )
}

/**
 * Les séances que le masteur peut proposer en rattrapage (`buildSessionsList`) : dans les 7 jours
 * après l'absence, d'un live qui prend les rattrapages, sans remplacement, à moins de 3 élèves, sauf
 * celles qui ont déjà tous les élèves de la séance manquée ; rien à moins de 30 min de l'absence.
 */
export function makeupOptions(
    liveSessions: ReadonlyArray<ApiManagedLiveSession | null | undefined> | null | undefined,
    absenceDate: string,
    absenceSession: ApiManagedLiveSession | null | undefined,
    now: moment.Moment | Date | number = moment()
): TutorMakeupOption[] {
    const at = moment(now)
    const absence = moment(absenceDate)
    if (absence.diff(at, "minutes") < 30) return []
    const end = moment(absence).add(7, "days")
    const absentIds = (absenceSession?.students ?? []).map((student) => String(student?.id))

    const options: TutorMakeupOption[] = []
    for (const session of liveSessions ?? []) {
        if (session?.id == null || !supportsNoShowRecovery(session.live_room)) continue
        const students = session.students ?? []
        if (students.length >= 3) continue
        const sessionIds = students.map((student) => String(student?.id))
        if (absentIds.every((id) => sessionIds.includes(id))) continue
        for (const occurrence of session.next_occurrences ?? []) {
            if (!occurrence?.date) continue
            if (String(session.id) === String(absenceSession?.id) && occurrence.date === absenceDate) continue
            if (occurrence.substitution_id) continue
            const date = moment(occurrence.date)
            if (!date.isAfter(at) || !date.isBefore(end)) continue
            options.push({
                id: `${occurrence.date}|${session.id}`,
                date: date.format("YYYY-MM-DDTHH:mm:ss"),
                studentsLabel: session.students_list_string ?? "",
                payload: { liveSessionId: session.id, occurrenceDate: occurrence.date },
            })
        }
    }
    return options.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
}

/**
 * L'étape du rattrapage est sautée (`shouldSkipMakeupSteps`) : aucun créneau, moins de 30 min avant
 * la séance, ou la séance se vide (personne à rattraper).
 */
export function skipsMakeup(
    options: TutorMakeupOption[],
    absenceDate: string,
    impact: UITutorAbsenceImpactStudent[] | null,
    now: moment.Moment | Date | number = moment()
): boolean {
    return options.length === 0 || moment(absenceDate).diff(moment(now), "minutes") < 30 || emptiesSession(impact)
}

// ── Le déplacement d'un individuel ──────────────────────────────────────────

/** Les raisons d'un déplacement (`static-data.rescheduling_reasons`, par type). */
export function rescheduleReasons(
    reasons: { one_occurrence?: string[] | null; all_occurrences?: string[] | null } | null | undefined,
    moveKind: "one_occurrence" | "all_occurrences"
): UITutorRescheduleReason[] {
    return (reasons?.[moveKind] ?? []).filter(Boolean).map((label) => ({ id: label, label }))
}

/** Le jour (0 = lundi) et l'heure d'un créneau « Tuesday 17:00 » : le nouveau créneau part de l'ancien. */
export function slotOf(dayTime: string | null | undefined): { weekday: number | null; time: string | null } {
    const weekday = WEEKDAY_ORDER.indexOf(slotDayOf(dayTime ?? ""))
    const time = (dayTime ?? "").split(" ")[1]
    return {
        weekday: weekday >= 0 ? weekday : null,
        time: time && /^\d{2}:\d{2}$/.test(time) ? time : null,
    }
}

/** Le créneau tel que le serveur l'attend : « Tuesday 17:30 » (`l H:i`). */
export function dayTimeOf(slot: { weekday: number | null; time: string | null }): string | null {
    if (slot.weekday == null || slot.time == null) return null
    return `${WEEKDAY_ORDER[slot.weekday]} ${slot.time}`
}
